import { type ChangeEvent, type DragEvent, type RefObject, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { AppPrompt } from "../../components/AppPrompt";
import { useModalLayer } from "../../components/ModalLayer";
import { BoltIcon, CameraIcon, CheckIcon, ClearIcon, CrossIcon, PhotoIcon } from "../../components/review-icons";
import type { Rect, Size } from "../../core/services/scan-image";
import type { Capture } from "../../core/services/scan-session";
import { ScanCardActions, ScanCardState, type ScanKindChoice } from "./scan-card-model";
import { notRecognised, readyLine, sessionHeader, type TraySegment, traySegments, trayWord } from "./scan-tray";
import { LIVE_CAMERA_IN_APP, TORCH_IN_APP } from "./scanner";
import { guideLayout, panelBeside, promptPlace, roomBeside } from "./scanner-guide";

/**
 * The scanner, as iOS's scan mode: over the whole app, the live camera behind a card-shaped guide, the front and
 * back switch and the torch on top, and the session's tray, Import, the shutter and Done below. Files dropped on it
 * are imported.
 */
export function ScanCardPage() {
  const actions = useSubtree(ScanCardActions);
  const layer = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const view = useSize(layer);
  const covered = useSize(panel);
  useModalLayer(layer, actions.done);
  useLandingFocus();
  const scale = fontScale();
  const beside = panelBeside(view, scale);
  const room = beside ? roomBeside(view) : { width: view.width, height: view.height - covered.height };
  // The guide fits what the panel leaves, and the shutter crops what it draws; under the camera, once the panel is
  // measured there, not while its size is the one it had beside it.
  const measured = beside || (covered.height > 0 && covered.width >= view.width - 1);
  const layout = view.width > 0 && view.height > 0 && measured ? guideLayout(view, room, scale) : null;
  const guide = layout?.guide ?? null;

  const dropping = (e: DragEvent) => {
    if (e.dataTransfer.types.includes("Files")) e.preventDefault();
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    void actions.importPictures(Array.from(e.dataTransfer.files));
  };

  return createPortal(
    <div
      ref={layer}
      role="dialog"
      aria-modal="true"
      aria-label="Scan cards"
      tabIndex={-1}
      onDragOver={dropping}
      onDrop={drop}
      className="fixed inset-0 z-[60] overflow-hidden bg-black text-white outline-none"
    >
      <Preview guide={guide} promptInside={layout?.promptInside ?? false} roomWidth={room.width} />
      <TopBar right={beside ? view.width - room.width : 0} />
      <BottomPanel ref={panel} beside={beside} view={view} guide={guide} />
    </div>,
    document.body,
  );
}

/**
 * The scanner replaces the page that opened it, so its opener is gone when it closes: the focus goes to the page it
 * lands on, its heading, rather than to the document.
 */
function useLandingFocus() {
  useLayoutEffect(
    () => () => {
      requestAnimationFrame(() => {
        if (document.activeElement !== null && document.activeElement !== document.body) return;
        const heading = document.querySelector<HTMLElement>("main h1, main h2");
        if (heading === null) return;
        if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
        heading.focus({ preventScroll: true });
      });
    },
    [],
  );
}

/** The element's size, kept current; zero until it is laid out. */
function useSize(ref: RefObject<HTMLElement | null>): Size {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const measure = () =>
      setSize((s) =>
        s.width === el.clientWidth && s.height === el.clientHeight
          ? s
          : { width: el.clientWidth, height: el.clientHeight },
      );
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

/** The root font's size over the 16 px the guide's spacing is drawn for. */
function fontScale(): number {
  if (typeof getComputedStyle === "undefined") return 1;
  const size = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return size > 0 ? size / 16 : 1;
}

/** The camera with the guide, everything outside it dimmed, and the prompt under it; or why there is no camera. */
function Preview({ guide, promptInside, roomWidth }: { guide: Rect | null; promptInside: boolean; roomWidth: number }) {
  const state = useSubtree(ScanCardState);
  const actions = useSubtree(ScanCardActions);
  const observer = useObserver();
  const problem = observer.watch(state.cameraProblem);
  const prompt = observer.watch(state.prompt);
  const awaitingBack = observer.watch(state.heldFront) !== null;
  const place = guide === null ? null : promptPlace(guide, promptInside, roomWidth);
  // Inside the guide with the front taken, the chip over the guide says as much: the prompt is said, not drawn on it.
  const unseen = place?.growsUp === true && awaitingBack;

  return (
    <>
      {/* Mounted for the page's whole life: the camera starts with it and stops when it goes. */}
      <video
        ref={actions.cameraShown}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        playsInline
        muted
      />
      {problem !== null ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black p-8 text-center">
          <span className="text-white/20">
            <CameraIcon />
          </span>
          <p role="status" className="max-w-xs text-[13px] text-faint">
            {problem}
          </p>
        </div>
      ) : (
        guide !== null && (
          <>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute rounded-[10px] shadow-[0_0_0_100vmax_rgb(0_0_0/0.5)]"
              style={{ left: guide.x, top: guide.y, width: guide.width, height: guide.height }}
            />
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute"
              style={{ left: guide.x, top: guide.y }}
              width={guide.width}
              height={guide.height}
            >
              <rect
                x={1}
                y={1}
                width={guide.width - 2}
                height={guide.height - 2}
                rx={10}
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth={2}
                strokeDasharray="6 4"
              />
            </svg>
            {!unseen && (
              <p
                aria-hidden="true"
                className={`absolute left-0 px-4 text-center text-sm [text-shadow:0_0_6px_rgb(0_0_0/0.8)] ${awaitingBack ? "text-accent" : "text-white"}`}
                // In a short room it sits inside the guide, over its bottom edge, rather than under the panel.
                style={{
                  top: place?.y,
                  width: place?.width,
                  transform: place?.growsUp ? "translateY(-100%)" : undefined,
                }}
              >
                {/* Inside the guide it lies on the card: a dark pill keeps it readable there. */}
                <span
                  className={
                    place?.growsUp ? "rounded-full bg-black/70 px-3 py-1 [box-decoration-break:clone]" : undefined
                  }
                >
                  {prompt}
                </span>
              </p>
            )}
          </>
        )
      )}
      {/* Said from one node whatever the guide does (a turned screen draws none for a frame): a node put back is said again. */}
      {problem === null && (
        <p aria-live="polite" className="sr-only">
          {prompt}
        </p>
      )}
    </>
  );
}

/** ✕, Front only | Front & back, the torch, and the front waiting for its back; left of the panel set beside. */
function TopBar({ right }: { right: number }) {
  const state = useSubtree(ScanCardState);
  const actions = useSubtree(ScanCardActions);
  const observer = useObserver();
  const frontAndBack = observer.watch(state.frontAndBack);
  const torch = observer.watch(state.torch);
  const heldFront = observer.watch(state.heldFront);
  const kind = observer.watch(state.kind);
  const round = "flex size-10 shrink-0 items-center justify-center rounded-full bg-black/55";

  return (
    <div className="absolute top-0 left-0 px-4 pt-2" style={{ right }}>
      <div className="mx-auto flex max-w-lg items-center gap-2.5">
        <button type="button" aria-label="Close camera" onClick={actions.done} className={round}>
          <CrossIcon />
        </button>
        <fieldset className="flex min-w-0 flex-1 rounded-full bg-black/55 p-[3px]">
          <legend className="sr-only">Sides to shoot</legend>
          <Side label="Front only" selected={!frontAndBack} onSelect={() => actions.setFrontAndBack(false)} />
          <Side label="Front & back" selected={frontAndBack} onSelect={() => actions.setFrontAndBack(true)} />
        </fieldset>
        {torch === null ? (
          <span aria-hidden="true" className="size-10 shrink-0" />
        ) : (
          <button
            type="button"
            aria-label="Torch"
            aria-pressed={torch}
            onClick={() => void actions.toggleTorch()}
            className={`${round} ${torch ? "text-accent" : "text-white"}`}
          >
            <BoltIcon on={torch} />
          </button>
        )}
      </div>
      <fieldset className="mx-auto mt-2 flex w-fit rounded-full bg-black/55 p-[3px]">
        <legend className="sr-only">What you are scanning</legend>
        {(Object.keys(KIND_LABELS) as ScanKindChoice[]).map((option) => (
          <Side
            key={option}
            label={KIND_LABELS[option]}
            selected={kind === option}
            onSelect={() => actions.setKind(option)}
          />
        ))}
      </fieldset>
      {heldFront !== null && (
        <div className="mx-auto mt-1 flex w-fit items-center gap-1.5 rounded-full bg-black/55 px-2 py-1.5">
          <img src={heldFront} alt="" className="h-9 w-[26px] rounded-[4px] object-cover" />
          <span className="text-[11px] font-semibold text-white">Front taken</span>
          <button
            type="button"
            aria-label="Discard the front"
            onClick={actions.discardHeldFront}
            className="text-white/70"
          >
            <ClearIcon />
          </button>
        </div>
      )}
    </div>
  );
}

const KIND_LABELS: Record<ScanKindChoice, string> = { auto: "Auto", card: "Card", sealed: "Sealed" };

function Side({ label, selected, onSelect }: { label: string; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`flex-1 rounded-full px-3 py-[7px] text-[13px] font-semibold ${selected ? "bg-accent text-ink" : "text-accent"}`}
    >
      {label}
    </button>
  );
}

/** The session's header and tray, then Import, the shutter (or Take a photo) and Done: under the camera, or beside it. */
function BottomPanel({
  ref,
  beside,
  view,
  guide,
}: {
  ref: RefObject<HTMLDivElement | null>;
  beside: boolean;
  view: Size;
  guide: Rect | null;
}) {
  const state = useSubtree(ScanCardState);
  const actions = useSubtree(ScanCardActions);
  const observer = useObserver();
  const captures = observer.watch(state.captures);
  const ready = observer.watch(state.readyCount);
  const unrecognised = observer.watch(state.unrecognisedCount);
  const notice = observer.watch(state.notice);
  const noticeSaid = observer.watch(state.noticeSaid);
  const camera = observer.watch(state.camera);
  const problem = observer.watch(state.cameraProblem);
  const torch = observer.watch(state.torch);
  const importing = observer.watch(state.importing);
  const codeOutcome = observer.watch(state.codeOutcome);
  const imports = useRef<HTMLInputElement>(null);
  const photo = useRef<HTMLInputElement>(null);
  // Beside the camera the column over the controls may be scrolled to the tray: a notice put above it comes into view.
  const said = useRef<HTMLParagraphElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the same notice said again comes into view again
  useLayoutEffect(() => {
    const el = said.current;
    if (!beside || notice === null || el === null) return;
    // One taller than the column comes into view from its first words, the column alone scrolled: asking every
    // ancestor would move the whole scanner by what its layer overflows.
    const column = el.parentElement;
    if (column !== null && el.offsetHeight > column.clientHeight)
      column.scrollTop += el.getBoundingClientRect().top - column.getBoundingClientRect().top;
    else el.scrollIntoView({ block: "nearest" });
  }, [beside, notice, noticeSaid]);

  const picked = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    void actions.importPictures(files);
  };
  const inApp = camera === "failed" ? LIVE_CAMERA_IN_APP : camera === "on" && torch === null ? TORCH_IN_APP : null;

  return (
    <div
      ref={ref}
      className={
        beside
          ? "absolute top-0 right-0 bottom-0 flex w-[min(340px,48%)] flex-col bg-[linear-gradient(to_right,transparent,color-mix(in_srgb,var(--color-app)_92%,transparent)_12%,var(--color-app))] pt-3 pr-[env(safe-area-inset-right)] pb-[max(12px,env(safe-area-inset-bottom))] pl-4"
          : "absolute inset-x-0 bottom-0 bg-[linear-gradient(to_bottom,transparent,color-mix(in_srgb,var(--color-app)_92%,transparent)_22%,var(--color-app))] pt-7 pb-[max(12px,env(safe-area-inset-bottom))]"
      }
    >
      <div className={`mx-auto flex w-full max-w-lg flex-col gap-4 ${beside ? "min-h-0 flex-1" : ""}`}>
        {/* Beside the camera what is over the controls sits at the bottom, and scrolls whole when the screen is short:
            the shutter and Done stay on it. */}
        <div className={beside ? "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto" : "contents"}>
          <div className={`flex shrink-0 items-baseline justify-between gap-3 px-5 ${beside ? "mt-auto" : ""}`}>
            <p className="text-[11px] font-semibold tracking-[0.5px] text-faint">{sessionHeader(captures.length)}</p>
            <p role="status" className="text-xs text-muted">
              {readyLine(ready, unrecognised)}
            </p>
          </div>
          {codeOutcome === "unavailable" && (
            <div className="shrink-0 px-5">
              <button type="button" onClick={actions.retryBarcode} className="btn-secondary w-full">
                Try the barcode again
              </button>
            </div>
          )}
          {notice !== null && (
            <p key={noticeSaid} ref={said} role="alert" className="shrink-0 px-5 text-xs text-danger">
              {notice}
            </p>
          )}
          {captures.length > 0 && (
            <ul
              aria-label="This session's cards"
              className="flex shrink-0 gap-2.5 overflow-x-auto px-5 pt-3 [scrollbar-width:thin]"
            >
              {captures.map((capture) => (
                <TrayItem key={capture.id} capture={capture} />
              ))}
            </ul>
          )}
        </div>
        <BarcodeField />
        <div className="flex items-center justify-between gap-3 px-6">
          <button
            type="button"
            aria-label="Import from photos"
            aria-busy={importing}
            onClick={() => imports.current?.click()}
            className="flex size-[52px] shrink-0 items-center justify-center rounded-[var(--radius-btn)] border-2 border-accent text-accent"
          >
            {importing ? (
              <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            ) : (
              <PhotoIcon />
            )}
          </button>
          <input ref={imports} type="file" accept="image/*" multiple hidden onChange={picked} />
          {problem === null ? (
            // Held, not disabled, while the camera starts: a press then takes nothing, and the focus stays.
            <button
              type="button"
              aria-label="Take photo"
              aria-disabled={camera !== "on"}
              onClick={() => guide !== null && void actions.shoot(view, guide)}
              className={`size-[76px] shrink-0 rounded-full border-4 border-white p-0.5 ${camera === "on" ? "" : "opacity-40"}`}
            >
              <span className="block size-full rounded-full bg-white" />
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => photo.current?.click()}
                className="flex h-[52px] items-center gap-2 rounded-full border-2 border-white px-5 text-sm font-semibold"
              >
                <CameraIcon />
                Take a photo
              </button>
              <input ref={photo} type="file" accept="image/*" capture="environment" hidden onChange={picked} />
            </>
          )}
          <button
            type="button"
            onClick={actions.done}
            className="flex min-h-11 min-w-[52px] shrink-0 items-center justify-center rounded-[var(--radius-btn)] bg-accent px-3.5 text-sm font-semibold text-ink"
          >
            Done
          </button>
        </div>
        {inApp !== null && (
          <div className="px-5">
            <AppPrompt>{inApp}</AppPrompt>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A barcode typed, or sent by a barcode gun, which types into the focused field and presses Enter. It goes with the
 * next picture; looked up alone, a sealed box it names lands on Review with no photo.
 */
function BarcodeField() {
  const state = useSubtree(ScanCardState);
  const actions = useSubtree(ScanCardActions);
  const observer = useObserver();
  const barcode = observer.watch(state.barcode);
  const error = observer.watch(state.barcodeError);
  const canLookUp = observer.watch(state.canLookUp);

  return (
    <form
      className="px-5"
      onSubmit={(e) => {
        e.preventDefault();
        actions.lookUpBarcode();
      }}
    >
      <div className="flex gap-2">
        <input
          className="field min-w-0 flex-1"
          inputMode="numeric"
          autoComplete="off"
          aria-label="Barcode (optional)"
          placeholder="Barcode (optional): scan or type the UPC"
          value={barcode}
          aria-invalid={error !== null}
          onChange={(e) => actions.setBarcode(e.target.value)}
        />
        <button type="submit" disabled={!canLookUp} className="btn-secondary shrink-0">
          Look up
        </button>
      </div>
      {error !== null && <p className="mt-1 text-xs text-danger">{error}</p>}
    </form>
  );
}

const SEGMENT: Record<TraySegment, string> = {
  complete: "bg-accent",
  active: "bg-accent/45",
  failed: "bg-danger",
  pending: "bg-white/15",
};

/** One card of the session: its photo, a four-part bar for the pipeline and a word for where it is. */
function TrayItem({ capture }: { capture: Capture }) {
  const actions = useSubtree(ScanCardActions);
  const failed = capture.status === "failed";
  const unknown = notRecognised(capture);
  const ready = capture.status === "ready" && !unknown;
  const word = capture.codeOutcome !== null ? "Photo needed" : trayWord(capture);
  const border = failed ? "border-danger" : unknown ? "border-warn/55" : ready ? "border-accent/55" : "border-line";

  return (
    <li className="relative w-16 shrink-0">
      {/* A failed card retries on a press, as iOS's; any other is only shown. */}
      <button
        type="button"
        aria-label={failed ? ["Scan failed.", capture.error, "Retry"].filter(Boolean).join(" ") : word}
        aria-disabled={!failed}
        onClick={() => failed && actions.retry(capture.id)}
        className="flex w-16 flex-col items-center gap-1.5"
      >
        <span className="relative">
          {capture.previewUrl === null ? (
            // A barcode looked up with no photo.
            <span
              className={`flex h-[72px] w-[52px] items-center justify-center rounded-[var(--radius-thumb)] border bg-white/5 text-[10px] font-semibold text-muted ${border}`}
            >
              UPC
            </span>
          ) : (
            <img
              src={capture.previewUrl}
              alt=""
              className={`h-[72px] w-[52px] rounded-[var(--radius-thumb)] border object-cover ${border}`}
            />
          )}
          {(failed || ready || unknown) && (
            <span
              aria-hidden="true"
              className={`absolute -top-1.5 -right-1.5 flex size-[18px] items-center justify-center rounded-full border-2 border-app text-ink ${failed ? "bg-danger" : unknown ? "bg-warn" : "bg-accent"}`}
            >
              {failed ? (
                <span className="text-[10px] font-black leading-none">!</span>
              ) : unknown ? (
                <span className="text-[10px] font-black leading-none">?</span>
              ) : (
                <CheckIcon size={9} />
              )}
            </span>
          )}
        </span>
        <span aria-hidden="true" className="flex w-[52px] gap-[3px]">
          {traySegments(capture).map((segment, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the four stages, always in order
            <span key={i} className={`h-[3px] flex-1 rounded-sm ${SEGMENT[segment]}`} />
          ))}
        </span>
        <span
          aria-hidden="true"
          className={`max-w-full text-[11px] ${unknown ? "text-center leading-tight text-warn" : `truncate ${failed ? "text-danger" : ready ? "text-accent" : "text-muted"}`}`}
        >
          {word}
        </span>
      </button>
      {failed && (
        // A 24 px target around the 18 px mark, its corner past the photo's.
        <button
          type="button"
          aria-label="Discard"
          onClick={() => actions.discard(capture.id)}
          className="absolute -top-3 -left-2 flex size-6 items-center justify-center"
        >
          <span className="flex size-[18px] items-center justify-center rounded-full border-2 border-app bg-white/80 text-ink">
            <CrossIcon />
          </span>
        </button>
      )}
    </li>
  );
}
